import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { API_BASE_URL } from '@core/api/api.tokens';
import { suppressErrorToast } from '@core/errors/error.http-context';
import { UserService } from '@core/user/user.service';
import { distinctUntilChanged, firstValueFrom, map, merge, timeout } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TenantLogoService {
    private readonly http = inject(HttpClient);
    private readonly endpoint = inject(API_BASE_URL).replace(/\/+$/, '') + '/tenants/logo';
    readonly url = signal<string | null>(null);
    readonly error = signal('');
    readonly busy = signal(false);
    private generation = 0;
    private loadSequence = 0;
    constructor() {
        const users = inject(UserService);
        merge(users.user$.pipe(map((user) => user?.tenantId ?? '')), users.sessionEnded$.pipe(map(() => '')))
            .pipe(distinctUntilChanged())
            .subscribe((tenant) => {
                this.generation++;
                this.replace(null);
                this.error.set('');
                if (tenant) void this.reload();
            });
    }
    async reload(): Promise<void> {
        const generation = this.generation;
        const sequence = ++this.loadSequence;
        try {
            const blob = await firstValueFrom(
                this.http
                    .get(this.endpoint, {
                        responseType: 'blob',
                        context: suppressErrorToast(),
                    })
                    .pipe(timeout(15000))
            );
            if (generation !== this.generation || sequence !== this.loadSequence) return;
            if (!['image/png', 'image/jpeg'].includes(blob.type)) throw new Error('Ungültiges Logoformat');
            this.replace(URL.createObjectURL(blob));
            this.error.set('');
        } catch (error) {
            if (generation !== this.generation || sequence !== this.loadSequence) return;
            this.replace(null);
            this.error.set(
                error instanceof HttpErrorResponse && error.status === 404
                    ? ''
                    : 'Firmenlogo konnte nicht geladen werden.'
            );
        }
    }
    async upload(file: File): Promise<void> {
        if (!['image/png', 'image/jpeg'].includes(file.type) || file.size > 1024 * 1024)
            throw new Error('Bitte PNG oder JPG mit maximal 1 MB auswählen.');
        const body = new FormData();
        body.append('file', file);
        await this.mutate(() => firstValueFrom(this.http.put(this.endpoint, body).pipe(timeout(30000))));
    }
    async remove(): Promise<void> {
        await this.mutate(() => firstValueFrom(this.http.delete(this.endpoint).pipe(timeout(15000))));
    }
    private async mutate(action: () => Promise<unknown>): Promise<void> {
        if (this.busy()) throw new Error('Bitte den laufenden Vorgang abwarten.');
        const generation = this.generation;
        this.busy.set(true);
        try {
            await action();
            if (generation !== this.generation) throw new Error('Der Mandant hat sich geändert.');
            await this.reload();
            if (this.error()) throw new Error(this.error());
        } finally {
            this.busy.set(false);
        }
    }
    private replace(url: string | null): void {
        const previous = this.url();
        this.url.set(url);
        if (previous) URL.revokeObjectURL(previous);
    }
}
